import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { MessageCircleQuestion, Send, Sparkles } from "lucide-react";

export default function GuidedCoach() {
  const [sessionId] = useState(() => `s-${Date.now()}`);
  const [topic, setTopic] = useState("");
  const [messages, setMessages] = useState([
    { role: "coach", text: "Hi! I'm your writing coach. What are you working on today? Tell me about your topic or share what you're stuck on." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const onSend = async () => {
    if (!input.trim()) return;
    const text = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/coach/guided-question", { session_id: sessionId, user_message: text, topic });
      setMessages(m => [...m, { role: "coach", text: data.reply }]);
      celebrateBadges(data._meta);
    } catch { toast.error("Coach is offline"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-violet-100 text-violet-700 grid place-items-center"><MessageCircleQuestion size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Guided Coach</h1>
        </div>
        <p className="text-slate-600">A Socratic chat coach. I ask questions—you do the thinking.</p>
      </div>

      <div className="tactile-card p-4">
        <input data-testid="coach-topic" value={topic} onChange={(e)=>setTopic(e.target.value)}
          placeholder="(Optional) Topic context: e.g. 'character analysis of Atticus'"
          className="w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-violet-400 px-4 py-3 outline-none" />
      </div>

      <div className="tactile-card p-0 overflow-hidden">
        <div data-testid="coach-thread" className="h-[55vh] overflow-y-auto p-6 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-9 h-9 rounded-full grid place-items-center flex-shrink-0 ${m.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-violet-100 text-violet-700'}`}>
                {m.role === 'user' ? <span className="text-xs font-bold">YOU</span> : <Sparkles size={16}/>}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-900'}`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && <div className="text-sm text-slate-500 italic">Coach is thinking…</div>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-slate-100 p-3 flex gap-2">
          <input data-testid="coach-input" value={input} onChange={(e)=>setInput(e.target.value)}
            onKeyDown={(e)=>e.key==='Enter' && !loading && onSend()}
            placeholder="Type your thought..."
            className="flex-1 rounded-full border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-violet-400 px-5 py-3 outline-none" />
          <button data-testid="coach-send" onClick={onSend} disabled={loading || !input.trim()}
            className="bg-violet-600 hover:bg-violet-500 text-white font-bold rounded-full px-5 py-3 inline-flex items-center gap-2 disabled:opacity-60"
            style={{boxShadow:'0 4px 0 0 rgb(91 33 182)'}}>
            <Send size={16}/>
          </button>
        </div>
      </div>
    </div>
  );
}
